import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MediaCard from "@/components/feature/MediaCard";
import { MEDIA_TYPE_LABEL } from "@/mocks/media";
import { categoryPath, getMediaById, getRelatedMedia } from "@/lib/content";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getMediaById(id) : undefined;

  const { isFavorite, toggleFavorite } = useFavorites();
  const [copied, setCopied] = useState(false);

  const related = item ? getRelatedMedia(item, 6) : [];

  usePageMeta({
    title: item
      ? `${item.title} - 详细介绍与资讯 - po18`
      : "详情 - po18",
    description: item
      ? `${item.title}（${item.year}）${MEDIA_TYPE_LABEL[item.type]}：${item.synopsis.slice(0, 60)}… 更多${item.type === "variety" ? "综艺" : "影视"}资讯与推荐尽在 po18。`
      : "po18 内容详情页。",
    keywords: item
      ? `po18,${item.title},${MEDIA_TYPE_LABEL[item.type]},${item.genres.join(",")}`
      : "po18,内容详情",
  });

  if (!item) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-6 py-24 text-center">
          <span className="w-16 h-16 mx-auto rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl">
            <i className="ri-film-line" />
          </span>
          <h1 className="mt-5 text-xl font-bold text-foreground-900">
            未找到该内容
          </h1>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-line text-lg" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const fav = isFavorite(item.id);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const infoRows = [
    { label: "类型", value: MEDIA_TYPE_LABEL[item.type] },
    { label: "上映", value: `${item.year} 年` },
    { label: "地区", value: item.region },
    { label: "导演", value: item.director },
    { label: "主演", value: item.cast.join("、") },
    { label: "评分", value: item.rating.toFixed(1) },
  ];

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6">
        <nav className="pt-6 flex items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <Link
            to={categoryPath(item.type)}
            className="hover:text-primary-600 transition-colors cursor-pointer"
          >
            {MEDIA_TYPE_LABEL[item.type]}
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800 font-medium truncate max-w-[40vw]">
            {item.title}
          </span>
        </nav>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-8">
          {/* 主内容 */}
          <div className="min-w-0">
            <div className="grid grid-cols-1 sm:grid-cols-[240px_1fr] gap-6">
              <div className="w-full sm:w-60 aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={item.image}
                  alt={`${item.title} 海报`}
                  title={`${item.title} - ${MEDIA_TYPE_LABEL[item.type]}详情`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="min-w-0">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">
                  <i className="ri-price-tag-3-line" />
                  {MEDIA_TYPE_LABEL[item.type]}
                </span>
                <h1 className="mt-3 text-2xl md:text-4xl font-black text-foreground-950 leading-tight">
                  {item.title}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                  <span className="inline-flex items-center gap-1 text-accent-600 font-bold">
                    <i className="ri-star-fill" />
                    {item.rating.toFixed(1)}
                  </span>
                  <span className="text-sm text-foreground-500">
                    {item.year} · {item.region}
                  </span>
                  <span className="flex flex-wrap gap-1.5">
                    {item.genres.map((g) => (
                      <span
                        key={g}
                        className="px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium"
                      >
                        {g}
                      </span>
                    ))}
                  </span>
                </div>

                <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {infoRows.map((row) => (
                    <div key={row.label} className="flex gap-2 text-sm">
                      <dt className="text-foreground-400 w-12 shrink-0">{row.label}</dt>
                      <dd className="text-foreground-800 min-w-0">{row.value}</dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => toggleFavorite(item.id)}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      fav
                        ? "bg-primary-500 text-background-50"
                        : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                    }`}
                  >
                    <i className={fav ? "ri-heart-3-fill text-lg" : "ri-heart-3-line text-lg"} />
                    {fav ? "已收藏" : "收藏"}
                  </button>
                  <button
                    type="button"
                    onClick={copyLink}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-100 border border-background-200 text-foreground-700 text-sm font-semibold hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-link text-lg" />
                    {copied ? "链接已复制" : "分享链接"}
                  </button>
                  <Link
                    to={categoryPath(item.type)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-100 border border-background-200 text-foreground-700 text-sm font-semibold hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line text-lg" />
                    返回{MEDIA_TYPE_LABEL[item.type]}
                  </Link>
                </div>
              </div>
            </div>

            {/* 简介 */}
            <section className="mt-8 rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-7">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                <span className="w-7 h-7 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center">
                  <i className="ri-file-text-line" />
                </span>
                剧情简介
              </h2>
              <p className="mt-4 text-base text-foreground-700 leading-loose">
                {item.synopsis}
              </p>
            </section>

            {/* 相关推荐 */}
            <section className="mt-8">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 mb-5">
                <span className="w-7 h-7 rounded-md bg-accent-100 text-accent-600 flex items-center justify-center">
                  <i className="ri-movie-2-line" />
                </span>
                同类型推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-6">
                {related.slice(0, 3).map((r) => (
                  <MediaCard key={r.id} item={r} showType={false} />
                ))}
              </div>
            </section>
          </div>

          {/* 侧栏相关推荐 */}
          <aside className="min-w-0">
            <div className="lg:sticky lg:top-20 rounded-lg bg-background-100 border border-background-200/70 p-5">
              <h2 className="text-base font-bold text-foreground-900 mb-4">
                猜你想看
              </h2>
              <ul className="space-y-3">
                {related.map((r) => (
                  <li key={r.id}>
                    <Link
                      to={`/detail/${r.id}`}
                      className="group flex gap-3 cursor-pointer"
                    >
                      <div className="w-16 h-24 shrink-0 rounded-md overflow-hidden bg-background-200">
                        <img
                          src={r.image}
                          alt={`${r.title} 海报`}
                          title={`${r.title} - po18 推荐`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="min-w-0 py-1">
                        <p className="text-sm font-semibold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {r.title}
                        </p>
                        <p className="mt-1 text-xs text-foreground-400">
                          {r.year} · {r.genres.join("/")}
                        </p>
                        <span className="mt-1 inline-flex items-center gap-1 text-xs text-accent-600 font-semibold">
                          <i className="ri-star-fill text-[10px]" />
                          {r.rating.toFixed(1)}
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}